import { useState, useEffect, useContext } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Flex } from '@radix-ui/themes';

import CalendarView from '../campaigns/components/CampaignsTable/CalendarView/CalendarView.jsx';
import CreateHighlightModal from '../campaigns/components/CampaignsTable/CreateHighlightModal.jsx';
import ContentCalendarHeader from './components/ContentCalendarHeader.jsx';
import { ActionCableContext } from '../../lib/actionCableContext';
import { useNotification } from '../../shared/Notification/useNotification';

export function PlanTabsView() {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation('campaigns');
  const queryClient = useQueryClient();
  const cable = useContext(ActionCableContext);
  const { showSuccess, showError } = useNotification();

  const params = useParams();
  const routeYear =
    params.year && !isNaN(params.year) ? Number(params.year) : undefined;
  const routeMonth =
    params.month !== undefined && !isNaN(params.month)
      ? Number(params.month)
      : undefined;

  const [isHighlightModalOpen, setIsHighlightModalOpen] = useState(false);

  useEffect(() => {
    const channel = cable.subscriptions.create(
      { channel: 'CampaignChannel' },
      {
        received: data => {
          const { template, status, errors } = data;

          if (status === 'error') {
            showError({
              content: errors?.length
                ? errors.join(' ')
                : t('campaignNotifications.error'),
            });
            return;
          }

          const msg = template
            ? t('campaignNotifications.templateCreated')
            : t('campaignNotifications.copied');

          showSuccess({ content: msg });
          queryClient.invalidateQueries(['campaigns']);
          queryClient.invalidateQueries(['campaigns-by-year']);
        },
      },
    );
    return () => {
      channel.unsubscribe();
    };
  }, [cable, queryClient, showError, showSuccess, t]);

  const handleCalendarNavigate = (year, monthIndex) => {
    const currentParams = new URLSearchParams(location.search);
    const filterParams = new URLSearchParams();
    const hf = currentParams.get('hf');
    const bf = currentParams.get('bf');
    if (hf) filterParams.set('hf', hf);
    if (bf) filterParams.set('bf', bf);
    const qs = filterParams.toString();
    const suffix = qs ? `?${qs}` : '';

    if (monthIndex !== null && monthIndex !== undefined) {
      history.replace(`/calendar/${year}/${monthIndex}${suffix}`);
    } else {
      history.replace(`/calendar/${year}${suffix}`);
    }
  };

  const handleAddBroadcast = () => {
    const params = new URLSearchParams({ from: 'calendar' });
    if (routeYear) params.set('cy', routeYear);
    if (routeMonth !== undefined) params.set('cm', routeMonth);
    const currentParams = new URLSearchParams(location.search);
    const hf = currentParams.get('hf');
    const bf = currentParams.get('bf');
    if (hf) params.set('hf', hf);
    if (bf) params.set('bf', bf);
    window.location.href = `/client_admin/broadcast_messages#edit/audience?${params}`;
  };

  return (
    <Flex direction="column" maxWidth="1400px" m="5" gap="5">
      <ContentCalendarHeader
        onAddHighlight={() => setIsHighlightModalOpen(true)}
        onAddBroadcast={handleAddBroadcast}
      />
      <CalendarView
        selectedYearOverride={routeYear}
        selectedMonthOverride={routeMonth}
        onCalendarNavigate={handleCalendarNavigate}
      />
      <CreateHighlightModal
        isOpen={isHighlightModalOpen}
        onClose={() => setIsHighlightModalOpen(false)}
        onCreateHighlight={() => setIsHighlightModalOpen(false)}
      />
    </Flex>
  );
}

export default PlanTabsView;
